import { useState, useEffect, useMemo } from 'react';
import { formatCurrency, SEASONAL_COEFFICIENTS } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { TrendingUp, Calculator, ArrowRight, ChevronDown, ChevronUp, CheckCircle, Clock, BarChart3, Activity } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import OptimizeButton from '../../components/shared/OptimizeButton';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api` });

const MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const PatronPrevisionnel = () => {
  const [baseCA, setBaseCA] = useState(40000);
  const [ratioAchats, setRatioAchats] = useState(30);
  const [simulationCA, setSimulationCA] = useState('');
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [realData, setRealData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [histCompare, setHistCompare] = useState(null);
  const [useHistAvg, setUseHistAvg] = useState(true);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [prevRes, histRes] = await Promise.all([
          api.get(`/previsionnel/${selectedYear}`),
          api.get('/historique-ca/compare/all'),
        ]);
        setRealData(prevRes.data);
        setHistCompare(histRes.data);
      } catch { toast.error('Erreur chargement previsionnel'); }
      setLoading(false);
    };
    fetchData();
  }, [selectedYear]);

  const charges = realData?.charges || { salaires: 0, fixes: 0, total: 0 };
  const totalSalaires = charges.salaires;
  const totalFixes = charges.fixes;

  const forecast = useMemo(() => {
    return MONTHS.map((month, i) => {
      const real = realData?.months?.[i];
      const coef = SEASONAL_COEFFICIENTS[i + 1];
      const hasReal = real?.has_real_data || false;
      const isPast = real?.is_past || false;
      const isCurrent = real?.is_current || false;
      const histMoyenne = real?.hist_moyenne_ht || 0;
      const histDetail = real?.hist_detail || {};

      let caTTC;
      if (hasReal) {
        caTTC = real.ca_ttc;
      } else if (useHistAvg && histMoyenne > 0) {
        caTTC = histMoyenne * 1.1;
      } else {
        caTTC = baseCA * coef;
      }

      const caHT = caTTC / 1.1;
      const achats = (hasReal && real.achats_reel > 0) ? real.achats_reel : caHT * (ratioAchats / 100);
      const achatsIsReal = hasReal && real.achats_reel > 0;
      const tva = caTTC - caHT;
      const totalCharges = achats + totalSalaires + totalFixes;
      const benefice = caHT - achats - totalSalaires - totalFixes;
      const margeNet = caHT > 0 ? (benefice / caHT) * 100 : 0;
      const ratioAchatsReel = caHT > 0 ? (achats / caHT) * 100 : 0;
      return { month, monthShort: MONTHS_SHORT[i], coef, caTTC, caHT, achats, tva, totalCharges, benefice, margeNet, idx: i, hasReal, isPast, isCurrent, achatsIsReal, ratioAchatsReel, histMoyenne, histDetail, usedHist: !hasReal && useHistAvg && histMoyenne > 0 };
    });
  }, [baseCA, totalSalaires, totalFixes, ratioAchats, realData, useHistAvg]);

  const annualCA = forecast.reduce((s, f) => s + f.caTTC, 0);
  const annualBenefice = forecast.reduce((s, f) => s + f.benefice, 0);
  const moisRentables = forecast.filter(f => f.benefice > 0).length;
  const moisReels = forecast.filter(f => f.hasReal).length;
  const maxCA = Math.max(...forecast.map(f => f.caTTC), 1);

  const seuilRentabilite = useMemo(() => {
    const chargesMensuelles = totalSalaires + totalFixes;
    const sumCoefs = Object.values(SEASONAL_COEFFICIENTS).reduce((s, c) => s + c, 0);
    const factor = sumCoefs / 1.1 * (1 - ratioAchats / 100);
    return factor > 0 ? Math.ceil((12 * chargesMensuelles) / factor) : 0;
  }, [totalSalaires, totalFixes, ratioAchats]);

  const simulationResult = simulationCA ? (() => {
    const ca = parseFloat(simulationCA);
    const caHT = ca / 1.1;
    const achats = caHT * (ratioAchats / 100);
    return caHT - achats - totalSalaires - totalFixes;
  })() : null;

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4" data-testid="previ-loading">
      <Activity size={28} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-gold))' }} />
      <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="patron-previsionnel">
      {/* Header */}
      <div className="flex items-end justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Previsionnel</p>
          <div className="flex items-center gap-3">
            <button onClick={() => setSelectedYear(y => y - 1)} className="p-1 rounded-sm hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid="prev-year">
              <ChevronDown size={16} className="rotate-90" />
            </button>
            <h1 className="font-title text-2xl text-[rgb(var(--lm-text))] flex items-center gap-2" data-testid="previ-year">
              <TrendingUp size={20} style={{ color: 'rgb(var(--lm-gold))' }} /> {selectedYear}
            </h1>
            <button onClick={() => setSelectedYear(y => y + 1)} className="p-1 rounded-sm hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid="next-year">
              <ChevronDown size={16} className="-rotate-90" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setUseHistAvg(v => !v)}
            className={`text-[10px] px-2.5 py-1.5 rounded-sm border transition-colors ${useHistAvg ? 'border-[rgb(var(--lm-gold))] bg-[rgb(var(--lm-gold)/0.08)]' : 'border-[rgb(var(--lm-border))]'}`}
            style={useHistAvg ? { color: 'rgb(var(--lm-gold))' } : { color: 'rgb(var(--lm-text-muted))' }}
            data-testid="toggle-hist"
          >
            <BarChart3 size={10} className="inline mr-1" />
            Historique {useHistAvg ? 'ON' : 'OFF'}
          </button>
          {moisReels > 0 && (
            <span className="text-[10px] bg-[rgb(var(--lm-green)/0.08)] text-[rgb(var(--lm-green))] px-2 py-1 rounded-sm flex items-center gap-1">
              <CheckCircle size={10} /> {moisReels} reel(s)
            </span>
          )}
          <OptimizeButton page="previsionnel" mois={`${selectedYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`} />
        </div>
      </div>

      {/* Parametres */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="lm-card p-4 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[rgb(var(--lm-text))]">CA base mensuel</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Pour mois futurs (coef 1.0)</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" value={baseCA}
                onChange={e => setBaseCA(parseFloat(e.target.value) || 0)}
                className="w-28 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-right text-sm" data-testid="base-ca-input" />
              <span className="text-sm" style={{ color: 'rgb(var(--lm-gold))' }}>EUR</span>
            </div>
          </div>
        </div>
        <div className="lm-card p-4 animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[rgb(var(--lm-text))]">Ratio achats estime</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">% CA HT pour mois sans factures</p>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" value={ratioAchats} min={10} max={60}
                onChange={e => setRatioAchats(parseFloat(e.target.value) || 30)}
                className="w-20 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-right text-sm" data-testid="ratio-achats-input" />
              <span className="text-sm text-[rgb(var(--lm-text-secondary))]">%</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '180ms', animationFillMode: 'backwards' }} data-testid="kpi-ca-annuel">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">CA annuel</p>
          <p className="font-mono text-xl mt-1" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(annualCA)}</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">reel + prevu</p>
        </div>
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards' }} data-testid="kpi-benefice-annuel">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Benefice annuel</p>
          <p className={`font-mono text-xl mt-1 ${annualBenefice >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
            {formatCurrency(annualBenefice)}
          </p>
        </div>
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }} data-testid="kpi-seuil">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Seuil rentabilite</p>
          <p className={`font-mono text-xl mt-1 ${baseCA >= seuilRentabilite ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-orange))]'}`}>
            {formatCurrency(seuilRentabilite)}
          </p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">CA base min/mois</p>
        </div>
        <div className="lm-card p-5 text-center animate-slide-up" style={{ animationDelay: '360ms', animationFillMode: 'backwards' }} data-testid="kpi-mois-rentables">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Mois rentables</p>
          <p className={`font-mono text-xl mt-1 ${moisRentables >= 6 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-orange))]'}`}>
            {moisRentables}/12
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '420ms', animationFillMode: 'backwards' }} data-testid="forecast-chart">
        <p className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))] mb-5">CA & rentabilite {selectedYear}</p>
        <div className="flex items-end gap-1.5 h-40">
          {forecast.map((f, i) => {
            const barH = maxCA > 0 ? (f.caTTC / maxCA) * 100 : 0;
            const isProfit = f.benefice > 0;
            const isCurrent = f.isCurrent;
            let barBg = 'rgb(var(--lm-red) / 0.4)';
            if (isCurrent) barBg = 'rgb(var(--lm-gold))';
            else if (f.hasReal && isProfit) barBg = 'rgb(var(--lm-green))';
            else if (f.hasReal && !isProfit) barBg = 'rgb(var(--lm-red) / 0.7)';
            else if (isProfit) barBg = 'rgb(var(--lm-green) / 0.4)';
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`bar-${i}`}>
                <span className={`text-[9px] font-mono ${isProfit ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                  {f.benefice >= 0 ? '+' : ''}{(f.benefice / 1000).toFixed(0)}k
                </span>
                <div className="w-full relative" style={{ height: `${barH}%`, minHeight: '4px' }}>
                  <div className="absolute inset-0 rounded-t transition-all" style={{ background: barBg }} />
                </div>
                <span className={`text-[10px] ${isCurrent ? 'font-bold' : ''}`} style={{ color: isCurrent ? 'rgb(var(--lm-gold))' : f.hasReal ? 'rgb(var(--lm-text))' : 'rgb(var(--lm-text-muted))' }}>
                  {f.monthShort}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-3 text-[10px] text-[rgb(var(--lm-text-muted))] flex-wrap">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: 'rgb(var(--lm-green))' }} /> Reel rentable</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: 'rgb(var(--lm-green) / 0.4)' }} /> Prevu rentable</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: 'rgb(var(--lm-red) / 0.7)' }} /> Reel deficit</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: 'rgb(var(--lm-red) / 0.4)' }} /> Prevu deficit</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: 'rgb(var(--lm-gold))' }} /> En cours</span>
        </div>
      </div>

      {/* Table */}
      <div className="lm-card overflow-hidden animate-slide-up" style={{ animationDelay: '480ms', animationFillMode: 'backwards' }} data-testid="forecast-table">
        <div className="hidden md:grid grid-cols-11 gap-1 px-4 py-2 bg-[rgb(var(--lm-surface-elevated))] text-[10px] text-[rgb(var(--lm-text-muted))] uppercase">
          <div className="col-span-2">Mois</div>
          <div className="text-right">CA TTC</div>
          <div className="text-right">CA HT</div>
          <div className="text-right">Achats</div>
          <div className="text-right">Salaires</div>
          <div className="text-right">Fixes</div>
          <div className="text-right">TVA</div>
          <div className="text-right">Benefice</div>
          <div className="text-right">Marge</div>
          <div className="text-center">Source</div>
        </div>
        <div className="divide-y divide-[rgb(var(--lm-border))]">
          {forecast.map((f) => {
            const isExpanded = expandedMonth === f.idx;
            return (
              <div key={f.idx}>
                {/* Desktop */}
                <div className={`hidden md:grid grid-cols-11 gap-1 px-4 py-2.5 items-center transition-colors hover:bg-[rgb(var(--lm-surface-elevated)/0.5)]
                  ${f.isCurrent ? 'bg-[rgb(var(--lm-gold)/0.04)]' : f.hasReal ? 'bg-[rgb(var(--lm-green)/0.02)]' : ''}`}
                  style={f.isCurrent ? { borderLeft: '2px solid rgb(var(--lm-gold))' } : {}}
                  data-testid={`forecast-${f.idx + 1}`}>
                  <div className="col-span-2 flex items-center gap-2">
                    <span className={`text-sm ${f.isCurrent ? 'font-medium' : ''}`} style={{ color: f.isCurrent ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-text))' }}>{f.month}</span>
                    <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">x{f.coef}</span>
                    {f.isCurrent && <span className="text-[8px] bg-[rgb(var(--lm-gold)/0.15)] px-1.5 py-0.5 rounded-sm" style={{ color: 'rgb(var(--lm-gold))' }}>En cours</span>}
                  </div>
                  <div className="text-right font-mono text-sm" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(f.caTTC)}</div>
                  <div className="text-right font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{formatCurrency(f.caHT)}</div>
                  <div className="text-right font-mono text-xs text-[rgb(var(--lm-text))]">{formatCurrency(f.achats)}</div>
                  <div className="text-right font-mono text-xs text-[rgb(var(--lm-text))]">{formatCurrency(totalSalaires)}</div>
                  <div className="text-right font-mono text-xs text-[rgb(var(--lm-text))]">{formatCurrency(totalFixes)}</div>
                  <div className="text-right font-mono text-xs text-[rgb(var(--lm-text-muted))]">{formatCurrency(f.tva)}</div>
                  <div className={`text-right font-mono text-sm font-medium ${f.benefice >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                    {f.benefice >= 0 ? '+' : ''}{formatCurrency(f.benefice)}
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-mono px-1.5 py-0.5 rounded-sm ${f.benefice >= 0 ? 'bg-[rgb(var(--lm-green)/0.08)] text-[rgb(var(--lm-green))]' : 'bg-[rgb(var(--lm-red)/0.08)] text-[rgb(var(--lm-red))]'}`}>
                      {f.margeNet.toFixed(1)}%
                    </span>
                  </div>
                  <div className="text-center">
                    {f.hasReal ? (
                      <CheckCircle size={12} className="inline" style={{ color: 'rgb(var(--lm-green))' }} />
                    ) : f.isCurrent ? (
                      <Clock size={12} className="inline" style={{ color: 'rgb(var(--lm-gold))' }} />
                    ) : f.usedHist ? (
                      <span className="text-[10px]" style={{ color: 'rgb(var(--lm-gold))' }}>moy</span>
                    ) : (
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">prevu</span>
                    )}
                  </div>
                </div>

                {/* Mobile */}
                <button className={`md:hidden w-full flex items-center justify-between px-4 py-3 ${f.isCurrent ? 'bg-[rgb(var(--lm-gold)/0.04)]' : ''}`}
                  onClick={() => setExpandedMonth(isExpanded ? null : f.idx)}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${f.isCurrent ? 'font-medium' : ''}`} style={{ color: f.isCurrent ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-text))' }}>{f.monthShort}</span>
                    {f.hasReal && <CheckCircle size={10} style={{ color: 'rgb(var(--lm-green))' }} />}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(f.caTTC)}</span>
                    <span className={`font-mono text-xs ${f.benefice >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                      {f.benefice >= 0 ? '+' : ''}{(f.benefice / 1000).toFixed(1)}k
                    </span>
                    {isExpanded ? <ChevronUp size={14} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="md:hidden grid grid-cols-3 gap-2 px-4 pb-3 text-center">
                    <div><p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Achats</p><p className="font-mono text-xs">{formatCurrency(f.achats)}</p></div>
                    <div><p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Salaires</p><p className="font-mono text-xs">{formatCurrency(totalSalaires)}</p></div>
                    <div><p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Fixes</p><p className="font-mono text-xs">{formatCurrency(totalFixes)}</p></div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Annual total */}
          <div className="hidden md:grid grid-cols-11 gap-1 px-4 py-3 bg-[rgb(var(--lm-surface-elevated))] items-center">
            <div className="col-span-2 text-sm text-[rgb(var(--lm-text))] font-medium">TOTAL {selectedYear}</div>
            <div className="text-right font-mono text-sm font-bold" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(annualCA)}</div>
            <div className="text-right font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{formatCurrency(annualCA / 1.1)}</div>
            <div className="text-right font-mono text-xs">{formatCurrency(forecast.reduce((s, f) => s + f.achats, 0))}</div>
            <div className="text-right font-mono text-xs">{formatCurrency(totalSalaires * 12)}</div>
            <div className="text-right font-mono text-xs">{formatCurrency(totalFixes * 12)}</div>
            <div className="text-right font-mono text-xs text-[rgb(var(--lm-text-muted))]">{formatCurrency(forecast.reduce((s, f) => s + f.tva, 0))}</div>
            <div className={`text-right font-mono text-sm font-bold ${annualBenefice >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
              {annualBenefice >= 0 ? '+' : ''}{formatCurrency(annualBenefice)}
            </div>
            <div className="text-right">
              <span className={`text-xs font-mono px-1.5 py-0.5 rounded-sm font-medium ${annualBenefice >= 0 ? 'bg-[rgb(var(--lm-green)/0.08)] text-[rgb(var(--lm-green))]' : 'bg-[rgb(var(--lm-red)/0.08)] text-[rgb(var(--lm-red))]'}`}>
                {annualCA > 0 ? `${(annualBenefice / (annualCA / 1.1) * 100).toFixed(1)}%` : '0%'}
              </span>
            </div>
            <div className="text-center text-[10px] text-[rgb(var(--lm-text-muted))]">{moisReels} reel</div>
          </div>

          {/* Mobile total */}
          <div className="md:hidden flex items-center justify-between px-4 py-3 bg-[rgb(var(--lm-surface-elevated))]">
            <span className="text-sm text-[rgb(var(--lm-text))] font-medium">TOTAL {selectedYear}</span>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(annualCA)}</span>
              <span className={`font-mono text-xs font-bold ${annualBenefice >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                {annualBenefice >= 0 ? '+' : ''}{(annualBenefice / 1000).toFixed(0)}k
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bilan Gains / Pertes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Mois gagnants */}
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '500ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-green) / 0.3)' }} data-testid="mois-gagnants">
          <p className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-green))] mb-3 flex items-center gap-2">
            <TrendingUp size={14} /> Mois gagnants
          </p>
          <div className="space-y-2">
            {forecast.filter(f => f.benefice > 0).sort((a, b) => b.benefice - a.benefice).map(f => (
              <div key={f.idx} className="flex items-center justify-between p-2 rounded-lg bg-[rgb(var(--lm-green)/0.05)]">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[rgb(var(--lm-text))]">{f.month}</span>
                  {f.hasReal && <CheckCircle size={10} className="text-[rgb(var(--lm-green))]" />}
                </div>
                <div className="text-right">
                  <span className="font-mono text-sm text-[rgb(var(--lm-green))] font-medium">+{formatCurrency(f.benefice)}</span>
                  <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))] ml-2">({f.margeNet.toFixed(1)}%)</span>
                </div>
              </div>
            ))}
            {forecast.filter(f => f.benefice > 0).length === 0 && (
              <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-4">Aucun mois rentable</p>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-[rgb(var(--lm-border))] flex items-center justify-between">
            <span className="text-xs text-[rgb(var(--lm-text-muted))]">Total gains</span>
            <span className="font-mono text-lg font-bold text-[rgb(var(--lm-green))]">
              +{formatCurrency(forecast.filter(f => f.benefice > 0).reduce((s, f) => s + f.benefice, 0))}
            </span>
          </div>
        </div>

        {/* Mois déficitaires */}
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '560ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-red) / 0.3)' }} data-testid="mois-deficitaires">
          <p className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-red))] mb-3 flex items-center gap-2">
            <Activity size={14} /> Mois deficitaires
          </p>
          <div className="space-y-2">
            {forecast.filter(f => f.benefice <= 0).sort((a, b) => a.benefice - b.benefice).map(f => (
              <div key={f.idx} className="flex items-center justify-between p-2 rounded-lg bg-[rgb(var(--lm-red)/0.05)]">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[rgb(var(--lm-text))]">{f.month}</span>
                  {f.hasReal && <CheckCircle size={10} className="text-[rgb(var(--lm-green))]" />}
                </div>
                <div className="text-right">
                  <span className="font-mono text-sm text-[rgb(var(--lm-red))] font-medium">{formatCurrency(f.benefice)}</span>
                  <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))] ml-2">({f.margeNet.toFixed(1)}%)</span>
                </div>
              </div>
            ))}
            {forecast.filter(f => f.benefice <= 0).length === 0 && (
              <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-4">Aucun mois en deficit !</p>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-[rgb(var(--lm-border))] flex items-center justify-between">
            <span className="text-xs text-[rgb(var(--lm-text-muted))]">Total pertes</span>
            <span className="font-mono text-lg font-bold text-[rgb(var(--lm-red))]">
              {formatCurrency(forecast.filter(f => f.benefice <= 0).reduce((s, f) => s + f.benefice, 0))}
            </span>
          </div>
        </div>
      </div>

      {/* Résultat net annuel */}
      <div className={`lm-card p-5 animate-slide-up text-center ${annualBenefice >= 0 ? '' : ''}`}
        style={{ animationDelay: '580ms', animationFillMode: 'backwards', borderColor: annualBenefice >= 0 ? 'rgb(var(--lm-green) / 0.3)' : 'rgb(var(--lm-red) / 0.3)' }}
        data-testid="resultat-net-annuel">
        <p className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Resultat net annuel {selectedYear}</p>
        <p className={`font-mono text-3xl font-bold ${annualBenefice >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
          {annualBenefice >= 0 ? '+' : ''}{formatCurrency(annualBenefice)}
        </p>
        <div className="flex items-center justify-center gap-6 mt-3 text-sm">
          <span className="text-[rgb(var(--lm-text-muted))]">
            CA: <span className="font-mono" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(annualCA)}</span>
          </span>
          <span className="text-[rgb(var(--lm-text-muted))]">
            Charges: <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(annualCA - annualBenefice - forecast.reduce((s, f) => s + f.tva, 0))}</span>
          </span>
          <span className="text-[rgb(var(--lm-text-muted))]">
            Marge: <span className={`font-mono ${annualBenefice >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
              {annualCA > 0 ? `${(annualBenefice / (annualCA / 1.1) * 100).toFixed(1)}%` : '0%'}
            </span>
          </span>
        </div>
      </div>

      {/* Simulator + Charges */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '540ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-gold) / 0.2)' }} data-testid="simulator">
          <div className="flex items-center gap-2 mb-3">
            <Calculator size={14} style={{ color: 'rgb(var(--lm-gold))' }} />
            <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Simulateur rapide</span>
          </div>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mb-3">Si je fais X EUR de CA ce mois, quel benefice ?</p>
          <div className="flex gap-3 items-center">
            <Input type="number" value={simulationCA}
              onChange={e => setSimulationCA(e.target.value)}
              className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-sm flex-1"
              placeholder="CA TTC" data-testid="simulation-input" />
            <ArrowRight size={16} className="text-[rgb(var(--lm-text-muted))] shrink-0" />
            {simulationResult !== null ? (
              <p className={`font-mono text-lg font-bold shrink-0 ${simulationResult >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                {simulationResult >= 0 ? '+' : ''}{formatCurrency(simulationResult)}
              </p>
            ) : (
              <p className="text-sm text-[rgb(var(--lm-text-muted))] shrink-0">?</p>
            )}
          </div>
        </div>

        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '600ms', animationFillMode: 'backwards' }} data-testid="charges-summary">
          <p className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))] mb-3">Charges mensuelles fixes</p>
          <div className="flex justify-between items-center py-1.5">
            <span className="text-sm text-[rgb(var(--lm-text-secondary))]">Salaires + charges</span>
            <span className="font-mono text-sm text-[rgb(var(--lm-text))]">{formatCurrency(totalSalaires)}</span>
          </div>
          <div className="flex justify-between items-center py-1.5">
            <span className="text-sm text-[rgb(var(--lm-text-secondary))]">Charges fixes</span>
            <span className="font-mono text-sm text-[rgb(var(--lm-text))]">{formatCurrency(totalFixes)}</span>
          </div>
          <div className="flex justify-between items-center py-1.5 border-t border-[rgb(var(--lm-border))] pt-2 mt-1">
            <span className="text-sm text-[rgb(var(--lm-text))] font-medium">Total</span>
            <span className="font-mono text-sm font-bold" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(totalSalaires + totalFixes)}</span>
          </div>
        </div>
      </div>

      {/* Coefficients */}
      <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '660ms', animationFillMode: 'backwards' }}>
        <p className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))] mb-4">Coefficients saisonniers</p>
        <div className="grid grid-cols-12 gap-1">
          {MONTHS_SHORT.map((m, i) => {
            const coef = SEASONAL_COEFFICIENTS[i + 1];
            const isCurrent = i === currentMonth;
            const hasReal = realData?.months?.[i]?.has_real_data;
            return (
              <div key={i} className="text-center rounded-sm py-1.5" style={{
                background: isCurrent ? 'rgb(var(--lm-gold) / 0.1)' : hasReal ? 'rgb(var(--lm-green) / 0.04)' : 'rgb(var(--lm-surface-elevated))'
              }}>
                <p className="text-[10px]" style={{ color: isCurrent ? 'rgb(var(--lm-gold))' : hasReal ? 'rgb(var(--lm-green))' : 'rgb(var(--lm-text-muted))' }}>{m}</p>
                <p className={`font-mono text-xs ${isCurrent ? 'font-bold' : ''}`} style={{ color: isCurrent ? 'rgb(var(--lm-gold))' : 'rgb(var(--lm-text))' }}>{coef}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Historical Comparison */}
      {histCompare && histCompare.years.length > 1 && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '720ms', animationFillMode: 'backwards' }} data-testid="hist-compare">
          <p className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))] mb-4">Historique CA HT par annee</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase">
                  <th className="text-left py-2 pr-2">Mois</th>
                  {histCompare.years.map(yr => (
                    <th key={yr} className={`text-right py-2 px-1 ${yr === String(selectedYear) ? 'font-bold' : ''}`}
                      style={yr === String(selectedYear) ? { color: 'rgb(var(--lm-gold))' } : {}}>
                      {yr}
                    </th>
                  ))}
                  <th className="text-right py-2 pl-2" style={{ color: 'rgb(var(--lm-gold))' }}>Moy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--lm-border)/0.3)]">
                {histCompare.months.map(m => (
                  <tr key={m.mois} className="hover:bg-[rgb(var(--lm-surface-elevated)/0.3)]">
                    <td className="py-1.5 pr-2 text-[rgb(var(--lm-text-secondary))]">{m.mois_label}</td>
                    {histCompare.years.map(yr => {
                      const val = m[yr] || 0;
                      return (
                        <td key={yr} className={`text-right py-1.5 px-1 font-mono ${yr === String(selectedYear) ? 'font-medium' : ''}`}
                          style={yr === String(selectedYear) ? { color: 'rgb(var(--lm-gold))' } : { color: val > 0 ? 'rgb(var(--lm-text))' : 'rgb(var(--lm-text-muted))' }}>
                          {val > 0 ? `${(val / 1000).toFixed(1)}k` : '-'}
                        </td>
                      );
                    })}
                    <td className="text-right py-1.5 pl-2 font-mono font-medium" style={{ color: 'rgb(var(--lm-gold) / 0.7)' }}>
                      {m.moyenne > 0 ? `${(m.moyenne / 1000).toFixed(1)}k` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-[rgb(var(--lm-border))]">
                  <td className="py-2 pr-2 text-sm font-medium text-[rgb(var(--lm-text))]">Total</td>
                  {histCompare.years.map(yr => (
                    <td key={yr} className={`text-right py-2 px-1 font-mono text-sm font-medium ${yr === String(selectedYear) ? '' : ''}`}
                      style={yr === String(selectedYear) ? { color: 'rgb(var(--lm-gold))' } : { color: 'rgb(var(--lm-text))' }}>
                      {histCompare.totals[yr] > 0 ? `${(histCompare.totals[yr] / 1000).toFixed(0)}k` : '-'}
                    </td>
                  ))}
                  <td className="text-right py-2 pl-2 font-mono text-sm font-medium" style={{ color: 'rgb(var(--lm-gold))' }}>
                    {formatCurrency(Object.values(histCompare.totals).reduce((s, v) => s + v, 0) / histCompare.years.length)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatronPrevisionnel;
